import { useEffect, useId, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { signatureMethodLabels, signatureMethods, type SignatureMethod } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Segmented, Select, TextField, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { recordSignature, uploadContract, useOffers, useSignature, type FinancierRecordFields } from './api';
import { todayKey } from './form';
import styles from './financier.module.css';

/**
 * Finanzierer 07 „Finanzierungsvertrag unterschrieben?": method (digital/offline), „Unterschrieben am *" (≤ today),
 * „Bank" (chosen bank + the other offer banks), optional contract → `POST …/signature`, then `POST …/contract`.
 * Rendered as the overview card and inside `SignatureDialog`.
 */

const CONTRACT_ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';

export function ContractPicker({
  file,
  onFile,
  disabled,
  label = 'Scan oder Foto des Vertrags hierher ziehen',
}: {
  file: File | null;
  onFile: (file: File | null) => void;
  disabled?: boolean | undefined;
  label?: string | undefined;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  function drop(event: DragEvent<HTMLSpanElement>) {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    const dropped = event.dataTransfer.files[0];
    if (dropped) onFile(dropped);
  }
  return (
    <span
      className={clsx(styles.contractDrop, over && styles.contractDropOver)}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={CONTRACT_ACCEPT}
        className={styles.srOnly}
        tabIndex={-1}
        aria-label="Vertrag"
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.[0] ?? null;
          event.target.value = '';
          if (chosen) onFile(chosen);
        }}
      />
      {file ? <span className={styles.contractName}>{file.name}</span> : <span>{label}</span>}
      <span className={styles.contractLinks}>
        {file ? (
          <button type="button" className={styles.linkMuted} disabled={disabled} onClick={() => onFile(null)}>
            Entfernen
          </button>
        ) : null}
        <button
          type="button"
          className={styles.link}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Datei wählen
        </button>
      </span>
    </span>
  );
}

export interface SignatureFormProps {
  financing: FinancierRecordFields;
  /** `card`: the overview card with title and subtitle (FZ-07); `dialog`: the fields only, the dialog has the title. */
  variant: 'card' | 'dialog';
  onDone: () => void;
  /** dialog only: id for the external submit button. */
  formId?: string | undefined;
  onPendingChange?: ((pending: boolean) => void) | undefined;
}

export function SignatureForm({ financing, variant, onDone, formId, onPendingChange }: SignatureFormProps) {
  const ownId = useId();
  const id = formId ?? ownId;
  const signature = useSignature(financing.id);
  const offers = useOffers(financing.id);
  const { toast } = useToast();
  const [method, setMethod] = useState<SignatureMethod>('offline');
  const [signedOn, setSignedOn] = useState(todayKey());
  const [bank, setBank] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const defaults = signature.data?.defaults;
  useEffect(() => {
    if (!defaults) return;
    setMethod(defaults.method);
    setBank((current) => current || (defaults.bank ?? ''));
  }, [defaults?.method, defaults?.bank]);

  useEffect(() => onPendingChange?.(pending), [pending]);

  const banks = Array.from(
    new Set(
      [
        defaults?.bank ?? null,
        ...(offers.data?.rows ?? []).filter((row) => row.status !== 'rejected').map((row) => row.bank),
      ].filter((value): value is string => Boolean(value)),
    ),
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const next: Record<string, string> = {};
    if (!signedOn) next['signedOn'] = 'Pflichtangabe.';
    if (!bank) next['bank'] = 'Pflichtangabe.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      await recordSignature(financing.id, { signedOn, bank, method });
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setErrors(error.fields);
      else setBanner(errorMessage(error));
      setPending(false);
      return;
    }
    if (file) {
      try {
        await uploadContract(financing.id, file);
      } catch (error) {
        toast({
          message: `Unterschrift erfasst. Der Vertrag wurde nicht hochgeladen: ${errorMessage(error)}`,
        });
      }
    }
    setPending(false);
    onDone();
  }

  const fields = (
    <>
      <div className={styles.methodBlock}>
        <span className={styles.formLabel} id={`${id}-method`}>
          Wie wurde unterschrieben?
        </span>
        <Segmented
          label="Wie wurde unterschrieben?"
          items={signatureMethods.map((value) => ({ value, label: signatureMethodLabels[value] }))}
          value={method}
          onChange={setMethod}
        />
      </div>
      <div className={styles.signGrid}>
        <TextField
          label="Unterschrieben am"
          required
          type="date"
          value={signedOn}
          max={todayKey()}
          onChange={(event) => setSignedOn(event.target.value)}
          error={errors['signedOn']}
        />
        <Select
          label="Bank"
          options={banks.map((value) => ({ value, label: value }))}
          placeholder={banks.length === 0 ? 'Keine Bank' : undefined}
          value={bank}
          onChange={(event) => setBank(event.target.value)}
          error={errors['bank']}
        />
        <div className={styles.contractField}>
          <span className={styles.formLabel}>
            Vertrag <span className={styles.optionalNote}>optional, nachreichbar</span>
          </span>
          <ContractPicker file={file} onFile={setFile} disabled={pending} />
        </div>
      </div>
    </>
  );

  if (variant === 'dialog') {
    return (
      <form id={id} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {fields}
        <span className={styles.footNote}>
          Ohne Vertrag entsteht eine Task „Vertrag nachreichen“ bei Ihnen. Die KVE-Frist prüft der Notar.
        </span>
      </form>
    );
  }

  return (
    <section
      className={clsx(styles.card, styles.cardLarge, styles.cardAccent)}
      aria-labelledby={`${id}-title`}
    >
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <div id={`${id}-title`} className={styles.cardTitle}>
            Finanzierungsvertrag unterschrieben?
          </div>
          <div className={styles.cardSubtitle}>
            Mit dem Datum steht die Finanzierung auf „Unterschrieben“. Das geht auch, wenn der Vertrag offline
            auf Papier unterschrieben wurde, etwa in der Filiale.
          </div>
        </div>
      </div>
      <form id={id} onSubmit={submit} noValidate>
        {banner ? (
          <Banner tone="danger" style={{ marginTop: 14 }}>
            {banner}
          </Banner>
        ) : null}
        {fields}
        <div className={styles.cardFooterPlain} style={{ marginTop: 18 }}>
          <span className={styles.footNote}>
            Ohne Vertrag entsteht eine Task „Vertrag nachreichen“ bei Ihnen. Die KVE-Frist prüft der Notar.
          </span>
          <span className={styles.footActions}>
            <Button type="submit" loading={pending}>
              Unterschrift erfassen
            </Button>
          </span>
        </div>
      </form>
    </section>
  );
}
